/**
 * User-Facing AI School Summary Tool
 * 
 * Displays pre-generated AI summaries for schools the user has access to.
 * Simplified interface without admin controls - only drive time selection.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Chip,
  Autocomplete,
  TextField,
  Collapse
} from '@mui/material';
import {
  School as SchoolIcon,
  Download as DownloadIcon,
  AutoGraphOutlined as ProjectionsIcon,
  MapsHomeWorkOutlined as GeographicIcon,
  DonutSmallOutlined as CompetitiveIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  ExpandLess as ExpandLessIcon,
  Room as RoomIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import PageLayout from '../shared_components/pageLayout';
import EsriUpdateNote from '../shared_components/EsriUpdateNote';
import { useTeamSchoolCache } from '../../hooks/teamCacheService';
import { useSummaryCache } from '../../hooks/useSummaryCache';
import { getSummaryStatus, getUserStoredMultiSchoolSummaries } from '../../services/aiService';
import { useSnackbar } from 'notistack';
import { formatSchoolName } from '../../utils/nameFormatter';
import { useAISummaryAvailability } from '../../contexts/AISummaryContext';
import ReactMarkdown from 'react-markdown';
import { ESRI_CENSUS_YEAR, ESRI_CURRENT_YEAR, ESRI_PROJECTION_YEAR } from '../../constants/esriDataVintage';

// Reuse display components from admin
import GeographicSummaryTab from '../admin/ai_summary/GeographicSummaryTab';
import CompetitiveSummaryTab from '../admin/ai_summary/CompetitiveSummaryTab';
import ProjectionsSummaryTab from '../admin/ai_summary/ProjectionsSummaryTab';
import FormattedNarrative from '../admin/ai_summary/FormattedNarrative';
import ExportModal from '../admin/ai_summary/ExportModal';
import { exportSummaryToHTML } from '../../utils/htmlExport';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';

import { TRAVEL_MODE_LABELS } from '../../constants/travelModes';

const UserAISummary = () => {
  const trackUsage = useFeatureUsage(PRODUCT_AREAS.AI_SUMMARY_INSIGHTS);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAISummaries, isAdmin, aiSchools, portfolios: publishedPortfolios, loading: aiAvailabilityLoading } = useAISummaryAvailability();
  
  // Read school / portfolio parameters from URL
  const params = new URLSearchParams(location.search);
  const schoolIdFromUrl = params.get('school');
  const portfolioHashFromUrl = params.get('portfolio');
  
  // For admins, fall back to the full team school cache so they can browse all schools.
  // Non-admins use aiSchools from context (already filtered + loaded during auth).
  const { schools: allTeamSchools, loading: schoolsLoading } = useTeamSchoolCache();
  
  // Get summary cache
  const { getCachedSummary, loadSummaries: loadSummariesFromCache, preloadSummaries, isLoading: isCacheLoading, isDataLoading } = useSummaryCache();
  
  // Fallback protection: redirect if user doesn't have AI summaries
  // This should normally be caught by the route guard, but this is a safety net
  useEffect(() => {
    if (!aiAvailabilityLoading && !isAdmin && !hasAISummaries) {
      enqueueSnackbar(
        'AI summaries are not available yet. Contact your administrator for access.',
        { variant: 'info' }
      );
      navigate('/dashboard', { replace: true });
    }
  }, [hasAISummaries, isAdmin, aiAvailabilityLoading, navigate, enqueueSnackbar]);
  
  // State
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [explicitlyCleared, setExplicitlyCleared] = useState(false); // Track when user explicitly clears selection
  const [driveTime, setDriveTime] = useState(10);
  const [travelMode, setTravelMode] = useState('drive');
  
  
  // Summary data (now from cache)
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  
  // Status
  const [summaryStatus, setSummaryStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  
  // Export
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Recently viewed schools
  const [recentSchools, setRecentSchools] = useState([]);
  
  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    keyTakeaways: false
  });
  
  // Which explorer is expanded (only one at a time, full width)
  const [expandedExplorer, setExpandedExplorer] = useState(null);
  
  // Overview collapsed state
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);
  
  // Track previous drop-a-pin state and school to detect transitions
  const prevIsDropAPinRef = useRef(false);
  const prevSchoolUuidRef = useRef(null);
  const autoExpandedForSchoolRef = useRef(null); // Track which school we've auto-expanded for
  
  // Auto-collapse overview when an explorer is expanded
  useEffect(() => {
    if (expandedExplorer) {
      setOverviewCollapsed(true);
    } else {
      setOverviewCollapsed(false);
    }
  }, [expandedExplorer]);
  
  // Derived summary states from cache
  const geographicSummary = summaryData?.geographic || null;
  const competitiveSummary = summaryData?.competitive || null;
  const projectionsSummary = summaryData?.projections || null;
  const combinedSummary = summaryData?.combined || null;
  const overviewParsedSections = combinedSummary?.sections || combinedSummary?.debug?.parsed_sections || null;
  const hasKeyTakeaways = useMemo(() => {
    const sections = overviewParsedSections?.sections || [];
    if (sections.some((s) => (s.title || '').trim().toLowerCase() === 'key takeaways')) {
      return true;
    }
    const narrative = combinedSummary?.narrative || combinedSummary?.full_narrative || '';
    return /##\s*key takeaways/i.test(narrative);
  }, [overviewParsedSections, combinedSummary]);

  // Unified loading flag for the school list, regardless of path (admin vs non-admin)
  const listLoading = isAdmin ? schoolsLoading : aiAvailabilityLoading;

  // Format schools for display.
  // Non-admins: use aiSchools from context (pre-filtered, already loaded during auth).
  // Admins: use the full team school cache so they can browse any school.
  const formattedSchools = useMemo(() => {
    let source;
    if (isAdmin) {
      if (schoolsLoading) return [];
      // Build a lookup from the backend's aiSchools list which has the authoritative
      // is_drop_a_pin flag (requires both no NCES ID AND no enrollment).
      // Non-NCES schools with enrollment should NOT be treated as drop-a-pin.
      const aiSchoolMap = new Map(aiSchools.map(s => [s.uuid, s]));
      source = allTeamSchools.map(school => ({
        ...school,
        displayName: formatSchoolName(school.name),
        is_drop_a_pin: aiSchoolMap.has(school.uuid)
          ? aiSchoolMap.get(school.uuid).is_drop_a_pin
          : false,
      }));
    } else {
      if (aiAvailabilityLoading) return [];
      source = aiSchools.map(school => ({
        ...school,
        displayName: formatSchoolName(school.name),
        // Trust the backend flag entirely — non-NCES schools have no ncessch
        // but are not drop-a-pin locations if they have enrollment data.
        is_drop_a_pin: school.is_drop_a_pin,
      }));
    }
    return source.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [isAdmin, allTeamSchools, schoolsLoading, aiSchools, aiAvailabilityLoading]);

  const formattedPortfolios = useMemo(() => {
    const source = publishedPortfolios || [];
    const countsByTeam = source.reduce((acc, p) => {
      acc[p.team_id] = (acc[p.team_id] || 0) + 1;
      return acc;
    }, {});
    return source.map((portfolio) => {
      const teamName = portfolio.team_name || 'Portfolio';
      const needsCount = countsByTeam[portfolio.team_id] > 1;
      return {
        ...portfolio,
        kind: 'portfolio',
        uuid: `portfolio:${portfolio.school_set_hash}`,
        displayName: needsCount
          ? `${teamName} — Portfolio (${portfolio.school_count} schools)`
          : `${teamName} — Portfolio`,
      };
    });
  }, [publishedPortfolios]);

  const dropdownOptions = useMemo(
    () => [...formattedPortfolios, ...formattedSchools],
    [formattedPortfolios, formattedSchools]
  );

  const isPortfolioMode = Boolean(selectedPortfolio);
  const selectedOption = selectedPortfolio || selectedSchool;
  
  // Detect if selected school is a drop-a-pin location.
  // Rely solely on the backend-provided flag which checks both no NCES ID AND no enrollment.
  // Non-NCES schools have no ncessch but are real schools with enrollment, so we must not
  // fall back to checking ncessch alone.
  const isDropAPinLocation = useMemo(() => {
    if (!selectedSchool) return false;
    return selectedSchool.is_drop_a_pin ?? false;
  }, [selectedSchool]);
  
  
  // Load recently viewed schools from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ai_summary_recent_schools');
      if (stored) {
        const recentUUIDs = JSON.parse(stored);
        // Filter to only schools that are still available
        const validRecent = formattedSchools.filter(s => recentUUIDs.includes(s.uuid));
        setRecentSchools(validRecent.slice(0, 5)); // Keep max 5 recent
      }
    } catch (err) {
      console.error('Error loading recent schools:', err);
    }
  }, [formattedSchools]);
  
  // Initialize selectedSchool from URL parameter (for navigation from other pages)
  useEffect(() => {
    // Don't auto-select if user explicitly cleared the selection
    if (explicitlyCleared) {
      return;
    }
    
    // Only initialize if we have schools loaded and a URL parameter
    if (formattedSchools.length === 0 || !schoolIdFromUrl || portfolioHashFromUrl) {
      return;
    }
    
    // Don't override if already selected (unless URL changed)
    if (selectedSchool && selectedSchool.uuid === schoolIdFromUrl) {
      return;
    }
    
    // Find school by UUID or NCES ID (for backward compatibility)
    const school = formattedSchools.find(s => 
      s.uuid === schoolIdFromUrl || s.ncessch === schoolIdFromUrl
    );
    
    if (school) {
      console.log('[UserAISummary] Initializing school from URL:', school.displayName || school.name);
      setSelectedSchool(school);
      
      // Update recent schools list
      try {
        const stored = localStorage.getItem('ai_summary_recent_schools');
        let recentUUIDs = stored ? JSON.parse(stored) : [];
        
        // Remove if already exists, then add to front
        recentUUIDs = recentUUIDs.filter(uuid => uuid !== school.uuid);
        recentUUIDs.unshift(school.uuid);
        
        // Keep only last 5
        recentUUIDs = recentUUIDs.slice(0, 5);
        
        localStorage.setItem('ai_summary_recent_schools', JSON.stringify(recentUUIDs));
        
        // Update state
        const validRecent = formattedSchools.filter(s => recentUUIDs.includes(s.uuid));
        setRecentSchools(validRecent);
      } catch (err) {
        console.error('Error updating recent schools:', err);
      }
    } else if (schoolIdFromUrl) {
      console.warn('[UserAISummary] School not found for URL parameter:', schoolIdFromUrl);
    }
  }, [schoolIdFromUrl, portfolioHashFromUrl, formattedSchools, selectedSchool, explicitlyCleared]);
  
  // Update recently viewed when school is selected
  const handlePortfolioSelect = (portfolio) => {
    if (!portfolio) return;
    setSelectedPortfolio(portfolio);
    setSelectedSchool(null);
    setExplicitlyCleared(false);
    setExpandedExplorer(null);
    setSummaryData(null);
    const combos = portfolio.combos || [];
    const defaultCombo = combos.find((c) => c.travel_mode === 'drive' && c.drive_time === 10)
      || combos.find((c) => c.travel_mode === 'drive')
      || combos[0];
    if (defaultCombo) {
      setTravelMode(defaultCombo.travel_mode || 'drive');
      setDriveTime(defaultCombo.drive_time);
    }
    const params = new URLSearchParams({ portfolio: portfolio.school_set_hash });
    if (defaultCombo) {
      params.set('drive_time', String(defaultCombo.drive_time));
      params.set('travel_mode', defaultCombo.travel_mode || 'drive');
    }
    navigate(`/ai-summary?${params.toString()}`, { replace: true });
  };

  const handleSchoolSelect = (school) => {
    if (!school) return;
    if (school.kind === 'portfolio') {
      handlePortfolioSelect(school);
      return;
    }
    
    setSelectedSchool(school);
    setSelectedPortfolio(null);
    setExplicitlyCleared(false); // Reset the flag when a school is selected
    
    // Update URL to reflect selected school
    navigate(`/ai-summary?school=${school.uuid}`, { replace: true });
    
    // Update recent schools
    try {
      const stored = localStorage.getItem('ai_summary_recent_schools');
      let recentUUIDs = stored ? JSON.parse(stored) : [];
      
      // Remove if already exists, then add to front
      recentUUIDs = recentUUIDs.filter(uuid => uuid !== school.uuid);
      recentUUIDs.unshift(school.uuid);
      
      // Keep only last 5
      recentUUIDs = recentUUIDs.slice(0, 5);
      
      localStorage.setItem('ai_summary_recent_schools', JSON.stringify(recentUUIDs));
      
      // Update state
      const validRecent = formattedSchools.filter(s => recentUUIDs.includes(s.uuid));
      setRecentSchools(validRecent);
    } catch (err) {
      console.error('Error saving recent school:', err);
    }
  };
  
  // Load summary status and reset travel mode/drive time ONLY when school changes.
  // preloadSummaries is intentionally excluded from deps — its reference changes on every
  // cache update (getCachedSummary depends on cache state), which would cause this effect
  // to re-run and reset travelMode to 'drive' every time a summary finishes loading.
  // Preloading is handled in a separate effect below.
  useEffect(() => {
    const loadStatus = async () => {
      if (!selectedSchool?.uuid) {
        if (!selectedPortfolio) setSummaryStatus(null);
        return;
      }
      
      setLoadingStatus(true);
      try {
        const status = await getSummaryStatus(selectedSchool.uuid);
        setSummaryStatus(status);

        // Pick the default travel mode (prefer 'drive', fall back to first available)
        const modes = Object.keys(status?.status || {});
        const defaultMode = modes.includes('drive') ? 'drive' : (modes[0] || 'drive');
        setTravelMode(defaultMode);

        // Pick the default drive time (first available for that mode, prefer 10)
        const times = Object.keys(status?.status?.[defaultMode] || {}).map(Number).sort((a, b) => a - b);
        if (times.length > 0) {
          setDriveTime(times.includes(10) ? 10 : times[0]);
        }
      } catch (err) {
        console.error('Error loading summary status:', err);
        setSummaryStatus(null);
      } finally {
        setLoadingStatus(false);
      }
    };
    
    loadStatus();
  }, [selectedSchool?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore a published portfolio from the URL
  useEffect(() => {
    if (explicitlyCleared || !portfolioHashFromUrl || formattedPortfolios.length === 0) {
      return;
    }
    if (selectedPortfolio?.school_set_hash === portfolioHashFromUrl) {
      return;
    }
    const portfolio = formattedPortfolios.find((p) => p.school_set_hash === portfolioHashFromUrl);
    if (portfolio) {
      handlePortfolioSelect(portfolio);
    }
  }, [formattedPortfolios, portfolioHashFromUrl, explicitlyCleared, selectedPortfolio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preload available summaries whenever the school, status, or travel mode changes.
  // Uses a ref so changes to preloadSummaries (caused by cache updates) don't re-trigger
  // this effect and don't interfere with the travelMode state set above.
  const preloadSummariesRef = useRef(preloadSummaries);
  useEffect(() => { preloadSummariesRef.current = preloadSummaries; });

  useEffect(() => {
    if (!selectedSchool?.uuid || !summaryStatus) return;
    const times = Object.keys(summaryStatus?.status?.[travelMode] || {}).map(Number).sort((a, b) => a - b);
    times.forEach(dt => {
      preloadSummariesRef.current(selectedSchool.uuid, dt, travelMode);
    });
  }, [selectedSchool?.uuid, summaryStatus, travelMode]);
  
  // Auto-expand geographic explorer for drop-a-pin locations
  useEffect(() => {
    const currentSchoolUuid = selectedSchool?.uuid;
    const wasDropAPin = prevIsDropAPinRef.current;
    const schoolChanged = currentSchoolUuid !== prevSchoolUuidRef.current && prevSchoolUuidRef.current !== null;
    
    // If transitioning FROM drop-a-pin TO regular school, reset explorer
    if (schoolChanged && wasDropAPin && !isDropAPinLocation) {
      setExpandedExplorer(null);
      autoExpandedForSchoolRef.current = null;
    }
    // If transitioning FROM regular TO regular, reset explorer
    else if (schoolChanged && !wasDropAPin && !isDropAPinLocation) {
      setExpandedExplorer(null);
      autoExpandedForSchoolRef.current = null;
    }
    // If this is a drop-a-pin location and we haven't auto-expanded for it yet, expand immediately
    else if (isDropAPinLocation && currentSchoolUuid && autoExpandedForSchoolRef.current !== currentSchoolUuid) {
      setExpandedExplorer('geographic');
      autoExpandedForSchoolRef.current = currentSchoolUuid;
    }
    
    // Update tracking refs
    prevIsDropAPinRef.current = isDropAPinLocation;
    prevSchoolUuidRef.current = currentSchoolUuid;
  }, [isDropAPinLocation, selectedSchool?.uuid]);
  
  // Load summaries from cache when school, drive time, or travel mode changes
  useEffect(() => {
    const loadSummaries = async () => {
      if (!selectedSchool?.uuid) {
        if (!selectedPortfolio) setSummaryData(null);
        return;
      }
      
      // Check cache first for instant load
      const cached = getCachedSummary(selectedSchool.uuid, driveTime, travelMode);
      if (cached) {
        setSummaryData(cached);
        return;
      }
      
      // Not in cache, load from API
      setLoadingSummaries(true);
      
      try {
        const data = await loadSummariesFromCache(selectedSchool.uuid, driveTime, { travelMode });
        setSummaryData(data);
      } catch (err) {
        console.error('Error loading summaries:', err);
        setSummaryData(null);
      } finally {
        setLoadingSummaries(false);
      }
    };
    
    loadSummaries();
  }, [selectedSchool?.uuid, selectedPortfolio, driveTime, travelMode, getCachedSummary, loadSummariesFromCache]);

  // Load stored portfolio rollups (no generate, no per-school viz data)
  useEffect(() => {
    const loadPortfolio = async () => {
      if (!selectedPortfolio?.school_set_hash) {
        return;
      }
      setLoadingSummaries(true);
      setLoadingStatus(false);
      try {
        const resp = await getUserStoredMultiSchoolSummaries(
          selectedPortfolio.school_set_hash,
          driveTime,
          travelMode
        );
        const stored = resp.summaries || {};
        const normalize = (row) => row ? {
          ...row,
          narrative: row.narrative || row.full_narrative || '',
          mode: 'stored',
          from_cache: true
        } : null;
        setSummaryData({
          geographic: normalize(stored.geographic),
          competitive: normalize(stored.competitive),
          projections: normalize(stored.projections),
          combined: normalize(stored.overview)
        });
      } catch (err) {
        console.error('Error loading portfolio summaries:', err);
        enqueueSnackbar(err.message || 'Failed to load portfolio summaries', { variant: 'error' });
        setSummaryData(null);
      } finally {
        setLoadingSummaries(false);
      }
    };
    loadPortfolio();
  }, [selectedPortfolio?.school_set_hash, driveTime, travelMode, enqueueSnackbar]);

  useEffect(() => {
    if (selectedSchool?.uuid) {
      trackUsage('summary_viewed', { schoolUuid: selectedSchool.uuid });
    }
  }, [selectedSchool?.uuid, trackUsage]);
  
  const handleExportClick = () => {
    if (!selectedSchool) {
      enqueueSnackbar('Please select a school', { variant: 'warning' });
      return;
    }
    
    if (!geographicSummary && !competitiveSummary && !projectionsSummary) {
      enqueueSnackbar('No summaries available to export', { variant: 'warning' });
      return;
    }
    
    setExportModalOpen(true);
  };
  
  const handleExportWithOptions = async (selectedSections) => {
    setExporting(true);
    
    try {
      const exportData = {
        geographic: selectedSections.geographic ? geographicSummary : null,
        competitive: selectedSections.competitive ? competitiveSummary : null,
        projections: selectedSections.projections ? projectionsSummary : null
      };
      
      exportSummaryToHTML(
        selectedSchool.displayName || selectedSchool.name,
        exportData.geographic,
        exportData.competitive,
        exportData.projections,
        { driveTime, grades: [], governance: [], schoolType: [] },
        {},
        isDropAPinLocation
      );
      
      enqueueSnackbar('Summary exported to HTML', { variant: 'success' });
      setExportModalOpen(false);
    } catch (err) {
      console.error('Error exporting:', err);
      enqueueSnackbar('Failed to export summary', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };
  
  const hasSummaries = geographicSummary || competitiveSummary || projectionsSummary;
  const isLoadingSummaries = loadingSummaries || (!isPortfolioMode && isCacheLoading(selectedSchool?.uuid, driveTime, travelMode));
  const isLoadingVisualizationData = !isPortfolioMode && selectedSchool?.uuid ? isDataLoading(selectedSchool.uuid, driveTime, travelMode) : false;
  
  // Derive available travel modes and drive times dynamically from the status response.
  // Status shape: { [travelMode]: { [driveTime]: { geographic, competitive, ... } } }
  const availableTravelModes = useMemo(() => {
    if (isPortfolioMode) {
      return [...new Set((selectedPortfolio.combos || []).map((c) => c.travel_mode || 'drive'))];
    }
    return Object.keys(summaryStatus?.status || {});
  }, [summaryStatus, isPortfolioMode, selectedPortfolio]);

  const availableDriveTimes = useMemo(() => {
    if (isPortfolioMode) {
      const times = (selectedPortfolio.combos || [])
        .filter((c) => (c.travel_mode || 'drive') === travelMode)
        .map((c) => Number(c.drive_time));
      return [...new Set(times)].sort((a, b) => a - b);
    }
    const times = Object.keys(summaryStatus?.status?.[travelMode] || {}).map(Number).sort((a, b) => a - b);
    return times;
  }, [summaryStatus, travelMode, isPortfolioMode, selectedPortfolio]);

  // Check if summaries are available for current travel mode + drive time
  const hasStoredSummaries = useMemo(() => {
    if (isPortfolioMode) {
      return Boolean(geographicSummary || competitiveSummary || projectionsSummary || combinedSummary);
    }
    if (!summaryStatus?.status) return false;
    const driveTimeStatus = summaryStatus.status?.[travelMode]?.[driveTime];
    if (!driveTimeStatus) return false;
    return Object.values(driveTimeStatus).some(s => s?.available);
  }, [summaryStatus, travelMode, driveTime, isPortfolioMode, geographicSummary, competitiveSummary, projectionsSummary, combinedSummary]);

  return (
    <PageLayout 
      title="AI School Summary"
      icon={<AssistantOutlinedIcon />}
      description="View AI-powered insights for your schools including geographic context, competitive landscape, and enrollment projections. These summaries provide key insights and implications that complement the interactive Geographic, Competitive, and Projections Explorers to increase understanding and take action."
    >
      <Box sx={{ mb: 4 }}>
        {/* School Selection - Compact after selection */}
        <Paper sx={{ 
          p: selectedOption ? 1.5 : 2, 
          mb: 2,
          transition: 'all 0.3s ease'
        }}>
          {selectedOption ? (
            // Compact view when school or portfolio is selected
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                {isPortfolioMode ? <GroupsIcon color="primary" /> : <SchoolIcon color="primary" />}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedOption.displayName}
          </Typography>
                {isPortfolioMode && (
                  <Chip size="small" label={`${selectedPortfolio.school_count} schools`} variant="outlined" />
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedSchool(null);
                    setSelectedPortfolio(null);
                    setSummaryData(null);
                    setExplicitlyCleared(true); // Set flag to prevent auto-reselection
                    // Clear URL parameter when changing schools
                    navigate('/ai-summary', { replace: true });
                  }}
                  sx={{ ml: 'auto' }}
                >
                  {isPortfolioMode ? 'Change' : 'Change School'}
                </Button>
              </Box>
              
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Travel Mode</InputLabel>
                <Select
                  value={travelMode}
                  onChange={(e) => {
                    const newMode = e.target.value;
                    setTravelMode(newMode);
                    // Clamp drive time to first available for the new mode
                    const times = Object.keys(summaryStatus?.status?.[newMode] || {}).map(Number).sort((a, b) => a - b);
                    if (times.length > 0 && !times.includes(driveTime)) {
                      setDriveTime(times.includes(10) ? 10 : times[0]);
                    }
                  }}
                  label="Travel Mode"
                >
                  {(availableTravelModes.length > 0 ? availableTravelModes : ['drive']).map(mode => (
                    <MenuItem key={mode} value={mode}>{TRAVEL_MODE_LABELS[mode] || mode}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Drive Time</InputLabel>
                <Select
                  value={availableDriveTimes.includes(driveTime) ? driveTime : (availableDriveTimes[0] || driveTime)}
                  onChange={(e) => setDriveTime(e.target.value)}
                  label="Drive Time"
                >
                  {(availableDriveTimes.length > 0 ? availableDriveTimes : [5, 10, 15]).map(time => (
                    <MenuItem key={time} value={time}>{time} minutes</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportClick}
                disabled={!hasSummaries || isPortfolioMode}
                startIcon={<DownloadIcon />}
                sx={{
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  fontWeight: 600,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.dark',
                  },
                }}
              >
                Export
              </Button>
              
              {/* Subtle indicator when visualization data is loading in background */}
              {isLoadingVisualizationData && (
                <Chip
                  size="small"
                  icon={<CircularProgress size={14} />}
                  label="Loading charts..."
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 24,
                    opacity: 0.7
                  }}
                />
              )}
            </Box>
          ) : (
            // Full expanded view for school selection
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                  <SchoolIcon /> Select School
                </Typography>
              </Box>
              
              {/* Show warning if no schools have AI summaries */}
              {!isAdmin && !listLoading && aiSchools.length === 0 && formattedPortfolios.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No schools with AI summaries found. Contact your administrator to generate AI summaries for your schools.
                </Alert>
              )}
          
          {/* School Selector Dropdown */}
          <Autocomplete
            fullWidth
            options={dropdownOptions}
            loading={listLoading}
            value={selectedOption || null}
            groupBy={(option) => option.kind === 'portfolio' ? 'Portfolio' : 'Schools'}
            onChange={(_, newValue) => {
              if (newValue) {
                handleSchoolSelect(newValue);
              } else {
                setSelectedSchool(null);
                setSelectedPortfolio(null);
                setExplicitlyCleared(true);
              }
            }}
            getOptionLabel={(option) => option.displayName || ''}
            isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
            filterOptions={(options, { inputValue }) => {
              const lower = inputValue.toLowerCase();
              if (!lower) return options;
              return options.filter(o =>
                o.displayName?.toLowerCase().includes(lower) ||
                o.city?.toLowerCase().includes(lower) ||
                o.state?.toLowerCase().includes(lower)
              );
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {option.kind === 'portfolio'
                    ? <GroupsIcon sx={{ fontSize: '1rem', color: 'primary.main', flexShrink: 0 }} />
                    : option.is_drop_a_pin
                    ? <RoomIcon sx={{ fontSize: '1rem', color: 'warning.dark', flexShrink: 0 }} />
                    : <SchoolIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
                  }
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                      {option.displayName}
                    </Typography>
                    {option.kind === 'portfolio' ? (
                      <Typography variant="caption" color="text.secondary">
                        {option.school_count} schools
                      </Typography>
                    ) : (option.city || option.state) && (
                      <Typography variant="caption" color="text.secondary">
                        {[option.city, option.state].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={formattedPortfolios.length ? 'Select a school or portfolio' : 'Select a School'}
                placeholder="Search by name or location..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {listLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: recentSchools.length > 0 && !selectedSchool ? 2 : 0 }}
          />

          {/* Recently Viewed — shown below dropdown when nothing is selected */}
          {recentSchools.length > 0 && !selectedSchool && !listLoading && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HistoryIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Recently Viewed
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {recentSchools.map(school => (
                  <Chip
                    key={school.uuid}
                    label={school.displayName}
                    onClick={() => handleSchoolSelect(school)}
                    icon={school.is_drop_a_pin ? <RoomIcon /> : <SchoolIcon />}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
            </>
          )}
        </Paper>

        {/* Status Message */}
        {selectedOption && !loadingStatus && !hasStoredSummaries && !isLoadingSummaries && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {isPortfolioMode
              ? `No portfolio rollup is available at ${driveTime}-minute ${TRAVEL_MODE_LABELS[travelMode]?.toLowerCase() || travelMode} time yet.`
              : `No AI summaries have been generated for this school at ${driveTime}-minute ${TRAVEL_MODE_LABELS[travelMode]?.toLowerCase() || travelMode} time yet. Please contact your administrator to request summary generation.`}
          </Alert>
        )}
        
        {/* Overview Section - Preview Style */}
        {selectedOption && hasStoredSummaries && (
          <>
            {/* Drop-a-Pin Location Banner */}
            {isDropAPinLocation && (
              <Alert severity="info" icon={<RoomIcon />} sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Geographic Location Analysis
                </Typography>
                <Typography variant="body2">
                  This is a drop-a-pin location for geographic market analysis. Enrollment projections and competitive analysis are not available for locations without enrollment data.
                </Typography>
              </Alert>
            )}

            {/* Overall Analysis - Hidden for Drop-a-Pin Locations */}
            {!isDropAPinLocation && (
            <Paper sx={{ mb: 3 }}>
              <Box 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: expandedExplorer ? 'pointer' : 'default',
                  '&:hover': expandedExplorer ? { bgcolor: 'action.hover' } : {}
                }}
                onClick={() => expandedExplorer && setOverviewCollapsed(!overviewCollapsed)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssistantOutlinedIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Overall Analysis
                        </Typography>
                  {expandedExplorer && (
                    <Button size="small" sx={{ ml: 1 }}>
                      {overviewCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </Button>
                  )}
                </Box>
                        {!isPortfolioMode && (
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<OpenInNewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/school-summary?school=${selectedSchool.uuid}`);
                  }}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Open School Summary
                        </Button>
                        )}
                      </Box>
              
              <Collapse in={!overviewCollapsed}>
                <Box sx={{ p: 3, pt: 1 }}>
                  <EsriUpdateNote align="left" sx={{ mb: 1.5 }} />
                  {loadingSummaries ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography sx={{ ml: 2 }} color="text.secondary">Loading summary...</Typography>
                    </Box>
                  ) : combinedSummary ? (
                <Box>
                  {/* Overview - Always Visible (exclude Executive Summary and Key Takeaways) */}
                  <Box sx={{ mb: 2 }}>
                      <FormattedNarrative 
                        narrative={combinedSummary.narrative || combinedSummary.full_narrative || combinedSummary.executive_summary || ''}
                        parsedSections={overviewParsedSections}
                        hideExecutiveSummary
                      excludeSections={hasKeyTakeaways ? ['Executive Summary', 'Key Takeaways'] : ['Executive Summary']}
                    />
                  </Box>
                  
                  {/* Key Takeaways - only when the overview actually has that section */}
                  {hasKeyTakeaways && (
                  <Box>
                    <Button
                      fullWidth
                      onClick={() => setExpandedSections(prev => ({ ...prev, keyTakeaways: !prev.keyTakeaways }))}
                      startIcon={expandedSections.keyTakeaways ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 1.5,
                        px: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '4px',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Key Takeaways
                      </Typography>
                    </Button>
                    <Collapse in={expandedSections.keyTakeaways}>
                      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                        <FormattedNarrative 
                          narrative={combinedSummary.narrative || combinedSummary.full_narrative || ''}
                          parsedSections={overviewParsedSections}
                          includeSections={['Key Takeaways']}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                  )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No overview available. View individual sections below for detailed analysis.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Paper>
            )}

            {/* Full-Width Expanded Explorer */}
            {expandedExplorer && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    {expandedExplorer === 'geographic' && <><GeographicIcon color="primary" /> Geographic Analysis</>}
                    {expandedExplorer === 'competitive' && <><CompetitiveIcon color="primary" /> Competitive Analysis</>}
                    {expandedExplorer === 'projections' && <><ProjectionsIcon color="primary" /> Projections Analysis</>}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isPortfolioMode && (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => {
                        if (expandedExplorer === 'geographic') navigate(`/geographic-explorer?school=${selectedSchool.uuid}`);
                        if (expandedExplorer === 'competitive') navigate(`/competitive-explorer?school=${selectedSchool.uuid}`);
                        if (expandedExplorer === 'projections') navigate(`/projections-explorer?school=${selectedSchool.uuid}`);
                      }}
                    >
                      Open Full Explorer
                    </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ExpandLessIcon />}
                      onClick={() => setExpandedExplorer(null)}
                    >
                      Show Less
                    </Button>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                {isPortfolioMode ? (
                  <FormattedNarrative
                    narrative={
                      (expandedExplorer === 'geographic' ? geographicSummary :
                        expandedExplorer === 'competitive' ? competitiveSummary :
                        projectionsSummary)?.narrative
                      || (expandedExplorer === 'geographic' ? geographicSummary :
                        expandedExplorer === 'competitive' ? competitiveSummary :
                        projectionsSummary)?.full_narrative
                      || ''
                    }
                    parsedSections={
                      (expandedExplorer === 'geographic' ? geographicSummary :
                        expandedExplorer === 'competitive' ? competitiveSummary :
                        projectionsSummary)?.sections
                    }
                  />
                ) : (
                  <>
                {expandedExplorer === 'geographic' && (
                  <GeographicSummaryTab
                    key={`geographic-${selectedSchool.uuid}-${travelMode}-${driveTime}`}
                    summary={geographicSummary}
                    loading={isLoadingSummaries}
                    visualizationLoading={isLoadingVisualizationData}
                    onGenerate={null}
                    onRegenerate={null}
                    schoolName={selectedSchool.displayName || selectedSchool.name}
                    selectedSchool={selectedSchool}
                    showAdminControls={false}
                    showDataSources={false}
                  />
                )}
                
                {expandedExplorer === 'competitive' && (
                  <CompetitiveSummaryTab
                    key={`competitive-${selectedSchool.uuid}-${travelMode}-${driveTime}`}
                    summary={competitiveSummary}
                    loading={isLoadingSummaries}
                    onGenerate={null}
                    onRegenerate={null}
                    schoolName={selectedSchool.displayName || selectedSchool.name}
                    selectedSchool={selectedSchool}
                    showAdminControls={false}
                    showDataSources={false}
                  />
                )}
                
                {expandedExplorer === 'projections' && (
                  <ProjectionsSummaryTab
                    key={`projections-${selectedSchool.uuid}-${travelMode}-${driveTime}`}
                    summary={projectionsSummary}
                    loading={isLoadingSummaries}
                    onGenerate={null}
                    onRegenerate={null}
                    schoolName={selectedSchool.displayName || selectedSchool.name}
                    selectedSchool={selectedSchool}
                    showAdminControls={false}
                    showDataSources={false}
                  />
                )}
                  </>
                )}
              </Paper>
            )}
            
            {/* Section Header for Explorer Cards */}
            <Box sx={{ mt: 4, mb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  textAlign: 'center'
                }}
              >
                {isDropAPinLocation ? 'Geographic Analysis' : 'Explore Deeper AI Insights'}
              </Typography>
            </Box>

            {/* Explorer Cards */}
            <Grid container spacing={2}>
              {/* Geographic Explorer Card */}
              {expandedExplorer !== 'geographic' && (
                <Grid item xs={12} md={isDropAPinLocation ? 12 : (expandedExplorer ? 6 : 4)}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.light'
                  }
                }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GeographicIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        Geographic
                      </Typography>
                    </Box>
                    
                    {geographicSummary && (
                      <Box sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        '& p': { 
                          margin: 0, 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          display: 'inline'
                        },
                        '& strong': { fontWeight: 600 }
                      }}>
                        <ReactMarkdown>{geographicSummary.executive_summary || 'Geographic analysis available'}</ReactMarkdown>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      onClick={() => setExpandedExplorer('geographic')}
                      startIcon={<ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Explore Deeper Insights
                    </Button>
                    {!isPortfolioMode && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/geographic-explorer?school=${selectedSchool.uuid}`)}
                      sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                      Open Explorer
                    </Button>
                    )}
                  </CardActions>
                </Card>
                </Grid>
              )}

              {/* Competitive Explorer Card - Hidden for Drop-a-Pin */}
              {!isDropAPinLocation && expandedExplorer !== 'competitive' && (
                <Grid item xs={12} md={expandedExplorer ? 6 : 4}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.light'
                  }
                }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CompetitiveIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        Competitive
                      </Typography>
                    </Box>
                    
                    {competitiveSummary && (
                      <Box sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        '& p': { 
                          margin: 0, 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          display: 'inline'
                        },
                        '& strong': { fontWeight: 600 }
                      }}>
                        <ReactMarkdown>{competitiveSummary.executive_summary || 'Competitive analysis available'}</ReactMarkdown>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      onClick={() => setExpandedExplorer('competitive')}
                      startIcon={<ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Explore Deeper Insights
                    </Button>
                    {!isPortfolioMode && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/competitive-explorer?school=${selectedSchool.uuid}`)}
                      sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                      Open Explorer
                    </Button>
                    )}
                  </CardActions>
                </Card>
                </Grid>
              )}

              {/* Projections Explorer Card - Hidden for Drop-a-Pin */}
              {!isDropAPinLocation && expandedExplorer !== 'projections' && (
                <Grid item xs={12} md={expandedExplorer ? 6 : 4}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.light'
                  }
                }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ProjectionsIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        Projections
                      </Typography>
                </Box>
                    
                    {projectionsSummary && (
                      <Box sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        '& p': { 
                          margin: 0, 
                          fontSize: '0.875rem', 
                          color: 'text.secondary',
                          display: 'inline'
                        },
                        '& strong': { fontWeight: 600 }
                      }}>
                        <ReactMarkdown>{projectionsSummary.executive_summary || 'Projections analysis available'}</ReactMarkdown>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      onClick={() => setExpandedExplorer('projections')}
                      startIcon={<ExpandMoreIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Explore Deeper Insights
                    </Button>
                    {!isPortfolioMode && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/projections-explorer?school=${selectedSchool.uuid}`)}
                      sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                      Open Explorer
                    </Button>
                    )}
                  </CardActions>
                </Card>
                </Grid>
              )}
            </Grid>
            
            {/* Compact Data Sources at Bottom */}
            {!isPortfolioMode && selectedSchool && (geographicSummary || competitiveSummary || projectionsSummary) && (
              <Box sx={{ 
                mt: 4, 
                pt: 2, 
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  flexWrap: 'wrap',
                  fontSize: '0.7rem'
                }}>
                  <strong>Data Sources:</strong>
                  {geographicSummary?.data_sources && (
                    <>
                      <span>Historical Enrollment: NCES 2017-2024</span>
                      <span>•</span>
                    </>
                  )}
                  {projectionsSummary?.data_sources && (
                    <>
                      <span>Projections: EDC Projections Model</span>
                      <span>•</span>
                    </>
                  )}
                  {(geographicSummary?.data_sources || competitiveSummary?.data_sources) && (
                    <span>Population: Esri {ESRI_CENSUS_YEAR}, {ESRI_CURRENT_YEAR}, {ESRI_PROJECTION_YEAR}</span>
                  )}
                </Typography>

                {/* ESRI data recency note */}
                {(geographicSummary?.data_sources || competitiveSummary?.data_sources || projectionsSummary?.data_sources) && (
                  <EsriUpdateNote align="left" sx={{ mt: 1 }} />
                )}
                
                {/* AI Disclaimer */}
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block',
                  mt: 1,
                  fontSize: '0.7rem',
                  fontStyle: 'italic',
                  opacity: 0.7
                }}>
                  AI can make mistakes and may miss school-specific details like phasing plans or capacity status. Please verify responses.
                </Typography>
            </Box>
            )}
          </>
        )}

        {/* Empty State */}
        {!selectedOption && !listLoading && (formattedSchools.length > 0 || formattedPortfolios.length > 0) && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AssistantOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.1rem' }}>
              {formattedPortfolios.length > 0
                ? 'Select a school or portfolio to view AI insights'
                : 'Select a school to view AI insights'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {recentSchools.length > 0 
                ? 'Choose from recently viewed or browse all schools above'
                : `Browse all ${formattedSchools.length} school${formattedSchools.length !== 1 ? 's' : ''} above to get started`
              }
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Export Modal */}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExportWithOptions}
        availableSections={{
          geographic: !!geographicSummary,
          competitive: !!competitiveSummary,
          projections: !!projectionsSummary
        }}
        exporting={exporting}
      />
    </PageLayout>
  );
};

export default UserAISummary;

